/**
 * Pure TEA update for history-review / trash-preview UI state.
 *
 * Shell: `useHistoryReview.ts` (review editor, panel open, layout).
 */
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";
import type { HistorySelection } from "../components/ChangeHistoryPanel.selection";

/** Same shape as session `TrashPreview` (kept local so update stays store-free). */
export type HistoryTrashPreview = {
  readonly label: string;
  readonly path: string;
  readonly content: string;
};

export type HistoryReviewModel = {
  readonly historySelection: Maybe<HistorySelection>;
  readonly trashPreview: Maybe<HistoryTrashPreview>;
};

export const initialHistoryReviewModel: HistoryReviewModel = {
  historySelection: nothing,
  trashPreview: nothing,
};

export type HistoryReviewMsg =
  | {
      readonly type: "ApplyHistorySelection";
      readonly selection: Maybe<HistorySelection>;
    }
  | {
      readonly type: "ExitHistoryReview";
      readonly restoreLiveDoc: boolean;
    }
  | {
      readonly type: "ShowTrashPreview";
      readonly content: string;
      readonly label: string;
      readonly path: string;
    }
  | {
      readonly type: "ClearTrashPreview";
      readonly restoreLiveDoc: boolean;
      readonly historyOpen: boolean;
    }
  | {
      readonly type: "HistorySelect";
      readonly selection: Maybe<HistorySelection>;
      readonly historyOpen: boolean;
    }
  | { readonly type: "HistoryRestore"; readonly content: string }
  | {
      readonly type: "ToggleHistory";
      readonly canShow: boolean;
      readonly mobileShell: boolean;
      readonly historyOpen: boolean;
    }
  | { readonly type: "CloseHistory" }
  | {
      readonly type: "FileSwitched";
      readonly canShowHistory: boolean;
      readonly historyOpen: boolean;
    };

export type HistoryReviewCmd =
  | { readonly kind: "syncReviewModeBridge" }
  | {
      readonly kind: "applySelectionToEditor";
      readonly selection: HistorySelection;
    }
  | {
      readonly kind: "exitHistoryReviewEffects";
      readonly restoreLiveDoc: boolean;
      readonly trashStillActive: boolean;
    }
  | {
      readonly kind: "showTrashPreviewEffects";
      readonly preview: HistoryTrashPreview;
    }
  | {
      readonly kind: "clearTrashPreviewEffects";
      readonly restoreLiveDoc: boolean;
    }
  | { readonly kind: "setHistoryOpen"; readonly open: boolean }
  | { readonly kind: "closeMobileChrome" }
  | {
      readonly kind: "notifyTrashPreviewChange";
      readonly preview: Maybe<HistoryTrashPreview>;
    }
  | { readonly kind: "restoreVersion"; readonly content: string }
  | { readonly kind: "applyRestoredDoc"; readonly content: string };

export function updateHistoryReview(
  model: HistoryReviewModel,
  msg: HistoryReviewMsg,
): readonly [HistoryReviewModel, readonly HistoryReviewCmd[]] {
  switch (msg.type) {
    case "ApplyHistorySelection": {
      if (isNothing(msg.selection)) {
        return [
          { ...model, historySelection: nothing },
          [
            { kind: "syncReviewModeBridge" },
            {
              kind: "exitHistoryReviewEffects",
              restoreLiveDoc: true,
              trashStillActive: isJust(model.trashPreview),
            },
          ],
        ];
      }
      return [
        { ...model, historySelection: msg.selection },
        [
          { kind: "syncReviewModeBridge" },
          {
            kind: "applySelectionToEditor",
            selection: msg.selection.value,
          },
        ],
      ];
    }
    case "ExitHistoryReview":
      return [
        { ...model, historySelection: nothing },
        [
          { kind: "syncReviewModeBridge" },
          {
            kind: "exitHistoryReviewEffects",
            restoreLiveDoc: msg.restoreLiveDoc,
            trashStillActive: isJust(model.trashPreview),
          },
        ],
      ];
    case "ShowTrashPreview": {
      const preview: HistoryTrashPreview = {
        label: msg.label,
        path: msg.path,
        content: msg.content,
      };
      return [
        { historySelection: nothing, trashPreview: just(preview) },
        [
          { kind: "setHistoryOpen", open: false },
          { kind: "syncReviewModeBridge" },
          { kind: "notifyTrashPreviewChange", preview: just(preview) },
          { kind: "showTrashPreviewEffects", preview },
        ],
      ];
    }
    case "ClearTrashPreview": {
      if (isNothing(model.trashPreview)) return [model, []];
      const historyReviewActive =
        msg.historyOpen === true && isJust(model.historySelection);
      const cmds: HistoryReviewCmd[] = [
        { kind: "syncReviewModeBridge" },
        { kind: "notifyTrashPreviewChange", preview: nothing },
      ];
      if (historyReviewActive === false) {
        cmds.push({
          kind: "clearTrashPreviewEffects",
          restoreLiveDoc: msg.restoreLiveDoc,
        });
      }
      return [{ ...model, trashPreview: nothing }, cmds];
    }
    case "HistorySelect": {
      if (msg.historyOpen === false) {
        return [
          { ...model, historySelection: nothing },
          [{ kind: "syncReviewModeBridge" }],
        ];
      }
      return updateHistoryReview(model, {
        type: "ApplyHistorySelection",
        selection: msg.selection,
      });
    }
    case "HistoryRestore":
      return [
        { ...model, historySelection: nothing },
        [
          { kind: "syncReviewModeBridge" },
          {
            kind: "exitHistoryReviewEffects",
            restoreLiveDoc: false,
            trashStillActive: isJust(model.trashPreview),
          },
          { kind: "setHistoryOpen", open: false },
          { kind: "applyRestoredDoc", content: msg.content },
          { kind: "restoreVersion", content: msg.content },
        ],
      ];
    case "ToggleHistory": {
      if (msg.canShow === false) return [model, []];
      const cmds: HistoryReviewCmd[] = [];
      if (msg.mobileShell === true && msg.historyOpen === false) {
        cmds.push({ kind: "closeMobileChrome" });
      }
      const nextOpen = msg.historyOpen === false;
      cmds.push({ kind: "setHistoryOpen", open: nextOpen });
      if (nextOpen === false) {
        return [
          { ...model, historySelection: nothing },
          [...cmds, { kind: "syncReviewModeBridge" }],
        ];
      }
      return [model, cmds];
    }
    case "CloseHistory":
      return [
        { ...model, historySelection: nothing },
        [
          { kind: "setHistoryOpen", open: false },
          { kind: "syncReviewModeBridge" },
        ],
      ];
    case "FileSwitched": {
      const cmds: HistoryReviewCmd[] = [];
      let next = model;
      if (isJust(model.trashPreview)) {
        next = { ...next, trashPreview: nothing };
        cmds.push(
          { kind: "syncReviewModeBridge" },
          { kind: "notifyTrashPreviewChange", preview: nothing },
          { kind: "clearTrashPreviewEffects", restoreLiveDoc: true },
        );
      }
      if (msg.canShowHistory === false) {
        cmds.push({ kind: "setHistoryOpen", open: false });
        return [next, cmds];
      }
      if (msg.historyOpen === true) {
        next = { ...next, historySelection: nothing };
        cmds.push(
          { kind: "syncReviewModeBridge" },
          {
            kind: "exitHistoryReviewEffects",
            restoreLiveDoc: true,
            trashStillActive: false,
          },
        );
      }
      return [next, cmds];
    }
    default:
      return assertNever(msg);
  }
}

export const updateHistoryReviewFn: UpdateFn<
  HistoryReviewModel,
  HistoryReviewMsg,
  HistoryReviewCmd
> = updateHistoryReview;
